import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { shiftLogActions } from '..';
import { Snippet } from '../../services/dataModels';
import { updateRowStatus } from '../helpers';

interface FormState {
  dirty: boolean;
  valid: boolean;
}

interface EditingState {
  editedSnippet?: Snippet;
  formState: FormState;
  mode: 'readOnly' | 'edit';
}

const initialState: EditingState = {
  editedSnippet: undefined,
  formState: { dirty: false, valid: true },
  mode: 'edit',
};

const editingSlice = createSlice({
  name: 'editing',
  initialState: initialState,
  reducers: {
    editedSnippetChanged: (state, { payload }: PayloadAction<Snippet | null>) => {
      const response = payload ? { ...payload } : payload;
      if (state.editedSnippet && payload) {
        response.isEdited =
          response.isEdited ||
          !(response.enableComment && hasOnlyEditComments(state.editedSnippet, response));
      }
      state.editedSnippet = response;
    },
    formStateChanged: (state, { payload }: PayloadAction<FormState>) => {
      state.formState = payload;
    },
    setMode: (state, { payload }: PayloadAction<'readOnly' | 'edit'>) => {
      state.mode = payload;
    },
  },
  extraReducers: (builder) => {
    [shiftLogActions.activateStandingOrder, shiftLogActions.completeStandingOrder].forEach(
      (action) => {
        builder.addCase(action.fulfilled, (state, { payload }) => {
          updateRowStatus(
            state.editedSnippet,
            'StandingOrder',
            { key: 'standingOrderId', value: payload.standingOrderId },
            { key: 'statusDisplayName', value: payload.statusDisplayName },
          );
        });
      },
    );

    [
      shiftLogActions.startTask,
      shiftLogActions.pauseTask,
      shiftLogActions.resumeTask,
      shiftLogActions.cancelTask,
      shiftLogActions.completeTask,
      shiftLogActions.updateTaskPercentage,
    ].forEach((action) => {
      builder.addCase(action.fulfilled, (state, { payload }) => {
        const i = state.editedSnippet.data[0].rowData.findIndex((i) => i.taskId === payload.taskId);
        state.editedSnippet.data[0].rowData[i] = {
          ...payload,
        };
      });
    });
  },
});

export default editingSlice.reducer;

export const actions = {
  ...editingSlice.actions,
};

const hasOnlyEditComments = (prev: Snippet, curr: Snippet) =>
  curr.data && prev.data
    ? prev.data.every((s, si) =>
        s.rowData.every((r, ri) =>
          Object.keys(r).every(
            (rk) =>
              rk === 'Comments' ||
              (curr.data?.length > si &&
                curr.data[si].rowData.length > ri &&
                r[rk] === curr.data[si].rowData[ri][rk]),
          ),
        ),
      )
    : false;
